import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { CustomerShell } from "@/components/CustomerShell";
import { toast } from "sonner";
import { ChevronLeft, Loader2, BellRing, X } from "lucide-react";

const SC = {
    REQUESTED: "bg-slate-100 text-slate-700", "DEMAND VALIDATED": "bg-blue-50 text-blue-700",
    "PROFESSIONAL SEARCH": "bg-indigo-50 text-indigo-700", "COMING SOON": "bg-amber-50 text-amber-700",
    AVAILABLE: "bg-emerald-50 text-emerald-700", "BOOK NOW": "bg-emerald-500 text-white", CANCELLED: "bg-red-50 text-red-700",
};

export default function MyRequests() {
    const [rows, setRows] = useState(null);
    const nav = useNavigate();
    const load = () => api.get("/demands").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const cancel = async (id) => {
        try { await api.post(`/demands/${id}/cancel`); toast.success("Request cancelled"); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    return (
        <CustomerShell>
            <div className="sticky top-0 z-20 flex items-center gap-3 bg-navy px-5 pb-4 pt-5 text-white">
                <button onClick={() => nav("/profile")} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><ChevronLeft size={20} /></button>
                <h1 className="font-heading text-lg font-bold">My Service Requests</h1>
            </div>
            <div className="px-5 py-5">
                {rows === null && <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                {rows && rows.length === 0 && (
                    <div className="mt-8 flex flex-col items-center text-center">
                        <div className="rounded-full bg-navy/5 p-5"><BellRing size={40} className="text-navy/30" /></div>
                        <h3 className="mt-4 font-heading text-lg font-bold text-navy">No requests yet</h3>
                        <p className="mt-1 text-sm text-navy/50">Request a "Coming Soon" service and we'll notify you when it launches.</p>
                    </div>
                )}
                <div className="space-y-3">
                    {rows?.map((d) => (
                        <div key={d.id} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`demand-${d.demand_id}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-heading text-sm font-extrabold text-navy">{d.service_name.replace(" Visit", "")}</span>
                                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${SC[d.status] || "bg-slate-100 text-slate-700"}`}>{d.status}</span>
                            </div>
                            <p className="mt-0.5 text-xs text-navy/50">{d.area_name}{d.society ? ` · ${d.society}` : ""} · {d.demand_id}</p>
                            <p className="text-xs text-navy/40">Requested {d.request_count}×{d.urgency ? ` · ${d.urgency}` : ""}</p>
                            {d.status === "BOOK NOW" ? (
                                <button data-testid={`book-now-${d.demand_id}`} onClick={() => nav("/book")} className="mt-3 w-full rounded-full bg-orange py-2.5 text-sm font-bold text-white active:scale-95">BOOK NOW</button>
                            ) : d.status !== "CANCELLED" && (
                                <button onClick={() => cancel(d.id)} className="mt-2 flex items-center gap-1 text-xs font-semibold text-navy/40 hover:text-red-500"><X size={13} /> Cancel request</button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </CustomerShell>
    );
}
